import type { BundleLine, CartLine } from '@/stores/cart.store';

/**
 * Slot-aware cart pricing.
 *
 * A rate plan can price a product by the hour (a pricing-schedule rule with
 * `timeRanges`: Go-Kart 18:00–23:00 dearer than daytime). The date-only price
 * map (`useResolvedProductPrices`) is priced against whatever slot is running
 * NOW, so a 19:00 ride picked at 06:00 read the daytime price until checkout.
 * These helpers collect every (product, slot) pair the cart holds so each can
 * be priced against its own slot — the same price the sale charges.
 */

export interface SlotPricePair {
  productId: string;
  slotId: string;
}

/** The key the backend's `slotPrices` map uses. */
export function slotPriceKey(productId: string, slotId: string): string {
  return `${productId}|${slotId}`;
}

/**
 * Every distinct (product, slot) pair in the cart: each seat's own slot, each
 * seat add-on with a slot, and each bundle extra with a slot. Bundle component
 * slots are left out — a bundle is priced as a whole, not per component.
 * Sorted, so the list is a stable query key.
 */
export function collectSlotPairs(lines: CartLine[], bundleLines: BundleLine[]): SlotPricePair[] {
  const seen = new Map<string, SlotPricePair>();
  const add = (productId: string, slotId: string | undefined) => {
    if (!productId || !slotId) return;
    seen.set(slotPriceKey(productId, slotId), { productId, slotId });
  };
  for (const l of lines) {
    for (const seat of l.seats) {
      add(l.productId, seat.slotId);
      for (const a of seat.addOns) add(a.productId, a.slotId);
    }
  }
  for (const b of bundleLines) for (const a of b.extraAddOns ?? []) add(a.productId, a.slotId);
  return [...seen.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([, p]) => p);
}
